import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { SynopsisModal } from './SynopsisModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MovieCard = ({ movie, isFavorite = false, onFavoriteToggle }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [favorited, setFavorited] = useState(isFavorite);
  const [loading, setLoading] = useState(false);
  const [showSynopsis, setShowSynopsis] = useState(false);
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  useEffect(() => {
    setFavorited(isFavorite);
  }, [isFavorite]);
  
  const posterUrl = movie.poster_url || 'https://via.placeholder.com/342x513?text=No+Image';
  const title = movie.title || 'Sans titre';
  const overview = movie.overview || 'Aucun synopsis disponible';
  const rating = movie.vote_average ? movie.vote_average.toFixed(1) : 'N/A';
  const actors = movie.actors || [];
  
  const handleCardClick = (e) => {
    // Only navigate if not clicking on favorite or synopsis button
    if (e.target.closest('[data-favorite-button]') || e.target.closest('[data-synopsis-button]')) {
      return;
    }
    
    navigate(`/film/${movie.id}`);
  };

  const handleSynopsisClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSynopsis(!showSynopsis);
  };
  
  const handleFavoriteClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      navigate('/login');
      return;
    }
    
    if (loading) return;
    
    setLoading(true);
    const newState = !favorited;
    setFavorited(newState);
    
    try {
      if (newState) {
        await axios.post(`${API}/favorites/${movie.id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.delete(`${API}/favorites/${movie.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      if (onFavoriteToggle) {
        setTimeout(() => onFavoriteToggle(), 100);
      }
    } catch (err) {
      setFavorited(!newState);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Card className="relative w-full bg-transparent border-none group">
      <div 
        className="relative overflow-hidden rounded-md cursor-pointer transition-transform duration-300 ease-out group-hover:scale-105 group-hover:-translate-y-2 group-hover:z-10"
        onClick={handleCardClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <img
          src={posterUrl}
          alt={title}
          className="w-full h-auto aspect-[2/3] object-cover"
          loading="lazy"
        />
        
        <div 
          className={`absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent transition-opacity duration-300 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="absolute bottom-0 left-0 right-0 p-4 text-white pointer-events-none">
            <h3 className="font-bebas text-xl md:text-2xl font-bold tracking-tight mb-2 line-clamp-2">
              {title}
            </h3>
            
            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center">
                <svg className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                  <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                </svg>
                <span className="ml-1 text-sm font-work font-semibold">{rating}</span>
              </div>
              {movie.release_date && (
                <span className="text-xs text-text-secondary font-work">
                  {new Date(movie.release_date).getFullYear()}
                </span>
              )}
            </div>
            
            {actors.length > 0 && (
              <p className="text-xs text-text-secondary font-inter mb-2">
                {actors.slice(0, 3).join(', ')}
              </p>
            )}
            
            <p className="text-xs md:text-sm text-text-secondary font-inter leading-relaxed line-clamp-2 mb-2">
              {overview}
            </p>
            
            <div className="text-netflix-red text-xs font-work font-semibold">
              Cliquez pour voir les détails →
            </div>
          </div>
        </div>
      </div>
      
      {/* Synopsis Button - Top left */}
      <button
        data-synopsis-button="true"
        onClick={handleSynopsisClick}
        className="absolute top-2 left-2 z-50 p-2.5 bg-black/90 hover:bg-blue-600 rounded-full transition-all shadow-xl border border-white/20"
        style={{ pointerEvents: 'auto' }}
      >
        <span className="text-xl">📝</span>
      </button>
      
      {/* Favorite Button - Top right */}
      <button
        data-favorite-button="true"
        onClick={handleFavoriteClick}
        disabled={loading}
        className="absolute top-2 right-2 z-50 p-2.5 bg-black/90 hover:bg-netflix-red rounded-full transition-all shadow-xl border border-white/20"
        style={{ pointerEvents: 'auto' }}
      >
        <Heart 
          className={`w-5 h-5 transition-all duration-200 ${
            favorited ? 'fill-netflix-red text-netflix-red' : 'text-white'
          } ${loading ? 'animate-pulse' : ''}`}
        />
      </button>

      {/* Synopsis Modal */}
      <SynopsisModal
        show={showSynopsis}
        onClose={() => setShowSynopsis(false)}
        title={title}
        overview={overview}
        genres={movie.genres}
        rating={rating}
        releaseDate={movie.release_date}
      />
    </Card>
  );
};
